import { ReactNode } from 'react'
import { Border, BoxConstraints, Font, PaintOp, RenderBox, RenderedAnnotation } from '../rendering'
import { renderElement } from '../document'
import { Box } from './box'
import { Text } from './text'

type CommonProps = {
  id?: string
  author?: string
  timestamp?: string
}

abstract class AppearanceAnnotationRenderBox<Props> extends RenderBox<Props> {
  performLayout(constraints: BoxConstraints): void {
    if (this.children.length != 1) throw new Error('Appearance annotation expects one child')

    const child = this.children[0]
    child.performLayout(constraints)
    child.box.x = 0
    child.box.y = 0
    const size = BoxConstraints.constrain(constraints, child.box)
    this.box = { x: 0, y: 0, ...size }
  }

  draw(): PaintOp {
    return { type: 'group', bounds: this.box, children: [] }
  }

  protected appearance(): PaintOp {
    return {
      type: 'group',
      bounds: { x: 0, y: 0, width: this.box.width, height: this.box.height },
      children: [this.children[0].draw()]
    }
  }
}

type CommentAnnotationProps = CommonProps & {
  content: string
  color?: string
}

class CommentAnnotationRenderBox extends RenderBox<CommentAnnotationProps> {
  performLayout(constraints: BoxConstraints): void {
    const size = BoxConstraints.constrain(constraints, { width: 20, height: 20 })
    this.box = { x: 0, y: 0, ...size }
  }

  annotation(): RenderedAnnotation {
    return {
      type: 'comment',
      id: this.props.id,
      bounds: this.box,
      author: this.props.author,
      timestamp: this.props.timestamp,
      content: this.props.content,
      color: this.props.color ?? '#FFCD46'
    }
  }
}

export function CommentAnnotation(props: CommentAnnotationProps) {
  return renderElement(new CommentAnnotationRenderBox(props))
}

type RectangleAnnotationProps = CommonProps & {
  border: Border
}

class RectangleAnnotationRenderBox extends AppearanceAnnotationRenderBox<RectangleAnnotationProps> {
  annotation(): RenderedAnnotation {
    return {
      type: 'rectangle',
      id: this.props.id,
      bounds: this.box,
      author: this.props.author,
      timestamp: this.props.timestamp,
      border: this.props.border,
      appearance: this.appearance()
    }
  }
}

export function RectangleAnnotation(props: RectangleAnnotationProps) {
  return renderElement(new RectangleAnnotationRenderBox(props), <Box border={props.border} />)
}

type TextAnnotationProps = CommonProps & {
  content: string
  color: string
  font: Font
}

class TextAnnotationRenderBox extends AppearanceAnnotationRenderBox<TextAnnotationProps> {
  annotation(): RenderedAnnotation {
    return {
      type: 'text',
      id: this.props.id,
      bounds: this.box,
      author: this.props.author,
      timestamp: this.props.timestamp,
      content: this.props.content,
      color: this.props.color,
      font: this.props.font,
      appearance: this.appearance()
    }
  }
}

export function TextAnnotation(props: TextAnnotationProps) {
  return renderElement(
    new TextAnnotationRenderBox(props),
    <Text
      fontFamily={props.font.family}
      fontSize={props.font.size}
      fontWeight={props.font.weight}
      fontStyle={props.font.style}
      color={props.color}
      lineHeight={1.1}
    >
      {props.content}
    </Text>
  )
}

type StampAnnotationProps = CommonProps & {
  children: ReactNode
}

class StampAnnotationRenderBox extends AppearanceAnnotationRenderBox<CommonProps> {
  annotation(): RenderedAnnotation {
    return {
      type: 'stamp',
      id: this.props.id,
      bounds: this.box,
      author: this.props.author,
      timestamp: this.props.timestamp,
      appearance: this.appearance()
    }
  }
}

export function StampAnnotation({ children, ...props }: StampAnnotationProps) {
  return renderElement(new StampAnnotationRenderBox(props), children)
}
